import {verifiedFetch} from '@github-ui/verified-fetch'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useState} from 'react'
import {NegativeFeedbackButton, PositiveFeedbackButton} from './Buttons'
import type {FeedbackSubmitHandler, FeedbackOptions} from './types'

interface CopilotCodeReviewFeedbackProps {
  commentId: string
  feedbackPath: string
  feedbackOptions: FeedbackOptions
  additionalParameters?: Record<string, string>
}

const CopilotCodeReviewFeedback: React.FC<CopilotCodeReviewFeedbackProps> = ({
  commentId,
  feedbackPath,
  feedbackOptions,
  additionalParameters,
}) => {
  const [typeSubmitted, setTypeSubmitted] = useState<'POSITIVE' | 'NEGATIVE'>()

  const handleClick: FeedbackSubmitHandler = (type, feedbackChoice, textResponse) => {
    setTypeSubmitted(type)
    try {
      const body = new FormData()
      body.set('comment_id', commentId)
      body.set('feedback', type)
      if (additionalParameters) {
        for (const [key, value] of Object.entries(additionalParameters)) {
          body.set(key, value)
        }
      }
      for (const choice of feedbackChoice ?? []) body.append('feedback_choice[]', choice)
      if (textResponse) body.set('text_response', textResponse)
      verifiedFetch(feedbackPath, {method: 'POST', body})
    } catch {
      // nothing users can really do here, so just fail silently
    }
  }

  return (
    <div data-testid={`copilot-code-review-feedback-${commentId}`} className="flex-1 d-flex gap-1">
      {typeSubmitted !== 'NEGATIVE' && <PositiveFeedbackButton disabled={!!typeSubmitted} onClick={handleClick} />}
      {typeSubmitted !== 'POSITIVE' && (
        <NegativeFeedbackButton disabled={!!typeSubmitted} onClick={handleClick} feedbackOptions={feedbackOptions} />
      )}
    </div>
  )
}

export const CopilotCodeReviewFeedbackWrapper: React.FC<CopilotCodeReviewFeedbackProps> = props => (
  <ErrorBoundary fallback={null}>
    <CopilotCodeReviewFeedback {...props} />
  </ErrorBoundary>
)

try{ CopilotCodeReviewFeedback.displayName ||= 'CopilotCodeReviewFeedback' } catch {}
try{ CopilotCodeReviewFeedbackWrapper.displayName ||= 'CopilotCodeReviewFeedbackWrapper' } catch {}